<script setup>
/**
 * Where each message in a running send has got to.
 *
 * ONLY NON-ZERO STATES ARE DRAWN. Nine chips of which seven read zero is a wall an owner
 * has to parse to find the two facts in it; the ones that are happening are the ones worth
 * her attention, and a state appearing is itself information.
 *
 * `unconfirmed` IS NOT A FAILURE AND IS NEVER COLOURED AS ONE. The reaper writes it when a
 * send was claimed but never confirmed, which means WhatsApp may have accepted and charged
 * for it — and the sidecar's "send again" deliberately excludes these rows for exactly that
 * reason. Colouring them ruby beside the real failures would invite an owner to retry them
 * and pay twice for messages that already went out, so they take amber and carry their own
 * sentence.
 *
 * `null` PROGRESS IS A STATE OF ITS OWN, not an empty one. The BFF answers null when the
 * breakdown could not be read while the broadcast itself loaded fine — zeros would have
 * been indistinguishable from a send where nothing has happened yet, which on a live run is
 * the one thing it must not be mistaken for.
 */
import { computed } from 'vue';

import { useWasloI18n } from '../../i18n.js';
import { toIntlLocale } from '../../helpers/intl.js';

const props = defineProps({
  /** All nine counts, or null when the breakdown could not be read. */
  progress: { type: Object, default: null },
});

const { t, locale } = useWasloI18n();

const ORDER = [
  { key: 'queued', copy: 'QUEUED', tone: 'bg-n-alpha-2 text-n-slate-11' },
  { key: 'sending', copy: 'SENDING', tone: 'bg-n-blue-3 text-n-blue-11' },
  { key: 'accepted', copy: 'ACCEPTED', tone: 'bg-n-alpha-2 text-n-slate-11' },
  { key: 'sent', copy: 'SENT', tone: 'bg-n-alpha-2 text-n-slate-11' },
  { key: 'delivered', copy: 'DELIVERED', tone: 'bg-n-blue-3 text-n-blue-11' },
  { key: 'read', copy: 'READ', tone: 'bg-n-blue-3 text-n-blue-11' },
  { key: 'failed', copy: 'FAILED', tone: 'bg-n-ruby-3 text-n-ruby-11' },
  { key: 'skipped', copy: 'SKIPPED', tone: 'bg-n-alpha-2 text-n-slate-11' },
  {
    key: 'unconfirmed',
    copy: 'UNCONFIRMED',
    tone: 'bg-n-amber-3 text-n-amber-11',
  },
];

const rows = computed(() => {
  if (!props.progress) return [];
  const format = new Intl.NumberFormat(toIntlLocale(locale.value));
  return ORDER.filter(row => props.progress[row.key] > 0).map(row => ({
    key: row.key,
    tone: row.tone,
    label: t(`WASLO.BROADCASTS.DETAIL.PROGRESS.${row.copy}`),
    value: format.format(props.progress[row.key]),
  }));
});

const hasUnconfirmed = computed(() => (props.progress?.unconfirmed ?? 0) > 0);
</script>

<template>
  <div class="flex flex-col gap-3" data-waslo-progress>
    <p v-if="!progress" class="mb-0 text-sm text-n-slate-11">
      {{ t('WASLO.BROADCASTS.DETAIL.PROGRESS.UNAVAILABLE') }}
    </p>
    <p v-else-if="rows.length === 0" class="mb-0 text-sm text-n-slate-11">
      {{ t('WASLO.BROADCASTS.DETAIL.PROGRESS.NOTHING') }}
    </p>
    <template v-else>
      <ul class="flex flex-wrap gap-2 mb-0 list-none">
        <li
          v-for="row in rows"
          :key="row.key"
          class="inline-flex items-center h-7 gap-2 px-2.5 text-xs font-medium rounded-lg"
          :class="row.tone"
          :data-waslo-progress-row="row.key"
        >
          <span>{{ row.label }}</span>
          <span class="tabular-nums">{{ row.value }}</span>
        </li>
      </ul>
      <p
        v-if="hasUnconfirmed"
        class="mb-0 text-xs text-n-slate-10 max-w-[68ch]"
        data-waslo-progress-unconfirmed-note
      >
        {{ t('WASLO.BROADCASTS.DETAIL.PROGRESS.UNCONFIRMED_NOTE') }}
      </p>
    </template>
  </div>
</template>
